import { useNavigate } from "react-router-dom";
import ImageDropPreview from "../../ImageDropPreview";
import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, Check, ArrowLeft, CloudUpload, X, AlertCircle, ExternalLink } from "lucide-react";
import { Header } from "../../Header";
import modelIcon from "../../../assets/icons/ai-model.png";
import { maleModels, femaleModels, posesImages, backgroundImages } from "./VirtualAiModelData";

import { useAuth } from "../../../AuthProvider";
import { useAccessControl } from "../../../AccessControlProvider";
import { postAiModel, getUsageSummary } from "../../../api/apiClient";
import { singleVirtualTryon, uploadImage, createBatchVirtualTryon, GarmentCategory, OutputQuality, AspectRatio } from "../../../api/virtualTryonClient";
import { createLogger } from "../../../utils/logger";

// Create logger instance for this component
const logger = createLogger('AiModel');

// Status enumeration
const JOB_STATUS = {
  INITIATED: 'INITIATED',
  IN_PROGRESS: 'IN-PROGRESS',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED'
};

// Maximum garments allowed in batch mode
const MAX_GARMENTS = 20;

const StepIndicator = ({ currentStep, steps }) => (
  <div className="w-full max-w-4xl mx-auto mb-8">
    <div className="flex items-center justify-between">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index < currentStep
                  ? "bg-blue-600 text-white"
                  : index === currentStep
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
            </div>
            <span
              className={`mt-2 text-sm ${
                index <= currentStep
                  ? "text-blue-600 font-medium"
                  : "text-gray-500"
              }`}
            >
              {step}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-1 mx-4 ${
                index < currentStep ? "bg-blue-600" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

const ModelCard = ({ image, isSelected, onClick }) => (
  <div
    className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all bg-gray-300 ${
      isSelected
        ? "border-blue-500 ring-2 ring-blue-200"
        : "border-gray-200 hover:border-gray-300"
    }`}
    onClick={onClick}
  >
    <img src={image} alt="Model" className="w-full h-48 sm:h-48 object-contain bg-gray-50" />
    {isSelected && (
      <div className="absolute top-2 right-2 bg-blue-600 rounded-full p-1">
        <Check className="w-4 h-4 text-white" />
      </div>
    )}
  </div>
);

const GarmentTypeButton = ({ icon, label, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={`cursor-pointer flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
      isSelected
        ? "bg-blue-50 border-blue-500 text-blue-700"
        : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
    }`}
  >
    <span className="text-lg">{icon}</span>
    <span className="font-medium">{label}</span>
  </button>
);

const GarmentCard = ({ image, onRemove, index }) => (
  <div className="relative rounded-lg overflow-hidden border-2 border-gray-200">
    <img src={image} alt={`Garment ${index + 1}`} className="w-full h-32 object-cover bg-gray-50" />
    <button
      onClick={onRemove}
      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 rounded-full p-1 transition-colors"
    >
      <X className="w-4 h-4 text-white" />
    </button>
    <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
      #{index + 1}
    </div>
  </div>
);

const DropzoneArea = ({ onDrop, acceptedFiles, isDragActive, uploadText, multiple = false }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
    },
    maxSize: 5242880, // 5MB
    multiple: multiple,
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed flex items-center justify-center flex-col gap-4 rounded-lg p-4 text-center cursor-pointer transition-colors ${
        isDragActive
          ? "border-blue-400 bg-blue-50"
          : "border-gray-300 bg-gray-50 hover:border-gray-400"
      }`}
    >
      <input {...getInputProps()} />
      <CloudUpload size={24} />
      <p className="text-gray-600 mb-2">{uploadText}</p>
      <button className="bg-white border flex items-center justify-center gap-3 border-gray-300 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
        <Upload size={20} /> Upload Image
      </button>
      <p className="text-sm text-gray-500 mt-2">
        Supported file formats: JPG, PNG, GIF, WebP - Maximum file size: 5 MB
      </p>
      {acceptedFiles.length > 0 && (
        <div className="mt-4">
          <p className="text-green-600 font-medium">
            ✓ {acceptedFiles[0].name} uploaded
          </p>
        </div>
      )}
    </div>
  );
};

const Ai_model = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [garmentFiles, setGarmentFiles] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [backgroundFiles, setBackgroundFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGarmentType, setSelectedGarmentType] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [selectedPoses, setSelectedPoses] = useState([]);
  const [generatedImages, setGeneratedImages] = useState(null);
  const [ApiResponse, setApiResponse] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [outputQuality, setOutputQuality] = useState('1K');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [qualityUpgradeMessage, setQualityUpgradeMessage] = useState('');
  const [processingMode, setProcessingMode] = useState('single'); // 'single' or 'batch'
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const { currentUser } = useAuth();
  const { privileges, loading: privilegesLoading } = useAccessControl();
  
  // Get user's output quality features and determine max quality
  const features = privilegesLoading ? [] : (privileges.privileges?.features || []);
  const outputFeatures = features.filter(f => f === '1k_output' || f === '2k_output' || f === '4k_output');
  
  // Determine the maximum quality available to the user
  const getMaxQuality = () => {
    if (outputFeatures.includes('4k_output')) return '4K';
    if (outputFeatures.includes('2k_output')) return '2K';
    return '1K'; // Default to 1K for all users
  };
  
  const maxQuality = getMaxQuality();
  
  // Map quality to numeric value for comparison
  const qualityValue = {
    '1K': 1,
    '2K': 2,
    '4K': 4
  };  

  // Check if user has batch processing permission
  const hasBatchPermission = !privilegesLoading && features.includes('batch_processing');

  // Fetch usage data for batch mode
  useEffect(() => {
    const fetchUsage = async () => {
      if (processingMode === 'batch' && currentUser) {
        try {
          setUsageLoading(true);
          const headers = {
            Authorization: `Bearer ${await currentUser.getIdToken()}`,
            "X-User-Id": currentUser.uid,
          };
          const data = await getUsageSummary({ headers });
          setUsageData(data);
        } catch (error) {
          console.error('Error fetching usage:', error);
        } finally {
          setUsageLoading(false);
        }
      }
    };
    fetchUsage();
  }, [processingMode, currentUser]);

  const steps = [
    "Choose Garment",
    "Select Model",
    "Pick Background",
    "Generate Look",
  ];

  const aimodel = 0;

  const [modelTab, setModelTab] = useState("female");

  const garmentTypes = [
    { id: "top", icon: "👔", label: "Top" },
    { id: "dress", icon: "👗", label: "Dress" },
    { id: "bottom", icon: "👖", label: "Bottom" },
    { id: "top_and_bottom", icon: "👕👖", label: "Top and Bottom" },
  ];

  const onGarmentDrop = useCallback((acceptedFiles) => {
    const files = Array.from(acceptedFiles);
    logger.info('Files dropped', { 
      count: files.length, 
      mode: processingMode,
      fileTypes: files.map(f => f.type)
    });
    
    if (processingMode === 'single') {
      // Single mode: accept only one file
      if (files.length > 0 && files[0].type.startsWith("image/")) {
        logger.debug('Processing single garment file', { 
          fileName: files[0].name,
          fileSize: files[0].size,
          fileType: files[0].type
        });
        
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
          const imageUrl = reader.result;
          logger.debug('Single garment file loaded successfully', { 
            dataUrlLength: imageUrl.length 
          });
          setGarmentFiles([imageUrl]); // Store as array for consistency
          setErrorMessage(null);
        };
        reader.onerror = (error) => {
          logger.error('Failed to read garment file', error);
          setErrorMessage('Failed to read the garment file');
        };
      }
    } else {
      // Batch mode: accept multiple files up to MAX_GARMENTS
      const validFiles = files.filter(file => file.type.startsWith("image/"));
      
      logger.debug('Batch mode file validation', {
        totalFiles: files.length,
        validFiles: validFiles.length,
        currentGarments: garmentFiles.length,
        maxGarments: MAX_GARMENTS
      });
      
      if (validFiles.length === 0) {
        logger.warn('No valid image files in batch upload');
        setErrorMessage('Please upload valid image files');
        return;
      }

      // Check if adding these files would exceed the limit
      if (garmentFiles.length + validFiles.length > MAX_GARMENTS) {
        const remaining = MAX_GARMENTS - garmentFiles.length;
        logger.warn('Batch upload would exceed maximum', {
          currentCount: garmentFiles.length,
          newFiles: validFiles.length,
          maxAllowed: MAX_GARMENTS,
          remaining
        });
        setErrorMessage(`Maximum ${MAX_GARMENTS} garments allowed. You can add ${remaining} more.`);
        return;
      }

      // Read all files and add them to garmentFiles
      logger.info('Reading batch garment files', { count: validFiles.length });
      const readFiles = validFiles.map((file, index) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            logger.debug(`Batch garment ${index + 1}/${validFiles.length} loaded`, {
              fileName: file.name,
              dataUrlLength: reader.result.length
            });
            resolve(reader.result);
          };
          reader.onerror = (error) => {
            logger.error(`Failed to read batch garment ${index + 1}`, error, {
              fileName: file.name
            });
            reject(error);
          };
        });
      });

      Promise.all(readFiles)
        .then(newGarments => {
          logger.info('All batch garments loaded successfully', { 
            count: newGarments.length,
            totalGarments: garmentFiles.length + newGarments.length
          });
          setGarmentFiles(prev => [...prev, ...newGarments]);
          setErrorMessage(null);
        })
        .catch(error => {
          logger.error('Failed to load some batch garments', error);
          setErrorMessage('Failed to load some garment files');
        });
    }
  }, [processingMode, garmentFiles]);

  const removeGarment = (index) => {
    setGarmentFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate total generations needed for batch mode
  const getTotalGenerations = () => {
    if (processingMode === 'single') return 1;
    const numGarments = garmentFiles.length;
    const numPoses = selectedPoses.length > 0 ? selectedPoses.length : 1;
    return numGarments * numPoses;
  };

  // Calculate available generations
  const getAvailableGenerations = () => {
    if (!usageData || !usageData.limits || !usageData.usage) return 0;
    const total = usageData.usage.total || 0;
    const limit = usageData.limits.limit || 0;
    return Math.max(0, limit - total);
  };

  // Check if user has enough usage
  const hasEnoughUsage = () => {
    if (processingMode === 'single') return true;
    if (usageLoading || !usageData) return true; // Allow if still loading
    const needed = getTotalGenerations();
    const available = getAvailableGenerations();
    return available >= needed;
  };

  // eslint-disable-next-line no-unused-vars
  const onBackgroundDrop = useCallback((acceptedFiles) => {
    setBackgroundFiles(acceptedFiles);
  }, []);

  const handlePoseSelection = (pose) => {
    setSelectedPoses((prevPoses) => {
      if (prevPoses.includes(pose)) {
        return prevPoses.filter((p) => p !== pose);
      } else {
        return [...prevPoses, pose];
      }
    });
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 0:
        // Require both a garment file and an explicit garment type selection
        return Boolean(garmentFiles && garmentFiles.length > 0) && Boolean(selectedGarmentType);
      case 1:
        // Both single and batch mode require poses to be selected
        return selectedPoses.length > 0;
      case 2:
        // Background selection is optional — allow the user to continue without choosing one
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    // Check usage before proceeding to final step in batch mode
    if (currentStep === 2 && processingMode === 'batch') {
      if (!hasEnoughUsage()) {
        const needed = getTotalGenerations();
        const available = getAvailableGenerations();
        setErrorMessage(
          `Insufficient usage limit. You need ${needed} generations (${garmentFiles.length} garments × ${selectedPoses.length} poses) but only have ${available} available. Please upgrade your plan or reduce the number of garments/poses.`
        );
        return;
      }
    }
    
    if (currentStep < steps.length - 1 && canProceedToNext()) {
      setCurrentStep(currentStep + 1);
      setErrorMessage(null);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrorMessage(null);
    }
  };

  const handleBatchGenerate = async () => {
    const batchStartTime = logger.startTimer('Batch Generation');
    logger.info('Starting batch generation', {
      garmentCount: garmentFiles.length,
      poseCount: selectedPoses.length,
      garmentType: selectedGarmentType,
      hasBackground: !!selectedBackground
    });

    try {
      // Validate that poses are selected
      if (selectedPoses.length === 0) {
        logger.warn('Batch generation validation failed: No poses selected');
        setErrorMessage('Please select at least one pose before generating.');
        setLoading(false);
        setIsGenerating(false);
        return;
      }

      // Validate usage one more time before generation
      if (!hasEnoughUsage()) {
        const needed = getTotalGenerations();
        const available = getAvailableGenerations();
        setErrorMessage(
          `Insufficient usage limit. You need ${needed} generations but only have ${available} available. Please upgrade your plan.`
        );
        setLoading(false);
        setIsGenerating(false);
        return;
      }

      const headers = {};
      if (currentUser) {
        headers["Authorization"] = `Bearer ${await currentUser.getIdToken()}`;
        headers["X-User-Id"] = currentUser.uid;
      }

      // Convert base64 images OR URLs to File objects for upload
      const imageToFile = async (imageSource, filename, mimeType = 'image/png') => {
        try {
          logger.debug('Converting image to file', { filename, mimeType, sourceType: imageSource.startsWith('data:') ? 'data-url' : 'url' });
          
          // If it's a regular URL (not a data URL), fetch it first
          if (!imageSource.startsWith('data:')) {
            logger.debug('Fetching image from URL', { filename });
            const response = await fetch(imageSource);
            if (!response.ok) {
              throw new Error(`Failed to fetch image: ${response.status}`);
            }
            const blob = await response.blob();
            const file = new File([blob], filename, { type: mimeType });
            logger.debug('Successfully converted URL to file', { 
              filename, 
              fileSize: file.size,
              fileType: file.type 
            });
            return file;
          }
          
          // Handle data URLs (base64)
          // Extract base64 data and clean it
          let base64Data = imageSource.includes(',') 
            ? imageSource.split(',')[1] 
            : imageSource;
          
          // Remove any whitespace, newlines, or invalid characters
          base64Data = base64Data.replace(/[\s\n\r]/g, '');
          
          // Validate base64 string
          if (!base64Data || base64Data.length === 0) {
            throw new Error('Empty base64 string');
          }
          
          const byteString = atob(base64Data);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: mimeType });
          const file = new File([blob], filename, { type: mimeType });
          
          logger.debug('Successfully converted base64 to file', { 
            filename, 
            fileSize: file.size,
            fileType: file.type 
          });
          
          return file;
        } catch (error) {
          logger.error('Failed to convert image to file', error, { 
            filename,
            sourceLength: imageSource?.length,
            sourcePreview: imageSource?.substring(0, 100),
            sourceType: imageSource?.startsWith('data:') ? 'data-url' : 'url'
          });
          throw new Error(`Failed to convert ${filename}: ${error.message}`);
        }
      };

      // Upload all garment files
      logger.info('Starting garment uploads', { count: garmentFiles.length });
      const garmentUrls = [];
      for (let i = 0; i < garmentFiles.length; i++) {
        const garment = garmentFiles[i];
        logger.debug(`Uploading garment ${i + 1}/${garmentFiles.length}`);
        
        const garmentFile = await imageToFile(garment, `garment_${Date.now()}_${i}.png`);
        const uploadResponse = await uploadImage({ 
          image: garmentFile, 
          purpose: 'garment',
          headers 
        });
        
        logger.debug(`Garment ${i + 1} uploaded successfully`, { url: uploadResponse.url });
        garmentUrls.push(uploadResponse.url);
      }
      logger.info('All garments uploaded successfully', { count: garmentUrls.length });

      // Determine which poses to use
      logger.info('Starting pose uploads', { count: selectedPoses.length });
      let poseUrls = [];
      if (selectedPoses.length > 0) {
        // Upload selected pose images
        for (let i = 0; i < selectedPoses.length; i++) {
          const pose = selectedPoses[i];
          logger.debug(`Uploading pose ${i + 1}/${selectedPoses.length}`);
          
          const poseFile = await imageToFile(pose, `pose_${Date.now()}_${i}.png`);
          const uploadResponse = await uploadImage({ 
            image: poseFile, 
            purpose: 'pose',
            headers 
          });
          
          logger.debug(`Pose ${i + 1} uploaded successfully`, { url: uploadResponse.url });
          poseUrls.push(uploadResponse.url);
        }
        logger.info('All poses uploaded successfully', { count: poseUrls.length });
      } else if (selectedModel) {
        // Use selected model as pose
        const modelFile = base64ToFile(selectedModel, `model_${Date.now()}.png`);
        const uploadResponse = await uploadImage({ 
          image: modelFile, 
          purpose: 'pose',
          headers 
        });
        poseUrls.push(uploadResponse.url);
      }

      // Upload background if selected
      let backgroundUrl = null;
      if (selectedBackground) {
        logger.info('Uploading background image');
        const backgroundFile = await imageToFile(selectedBackground, `background_${Date.now()}.png`);
        const uploadResponse = await uploadImage({ 
          image: backgroundFile, 
          purpose: 'background',
          headers 
        });
        backgroundUrl = uploadResponse.url;
        logger.info('Background uploaded successfully', { url: backgroundUrl });
      }

      // Create batch jobs for Gemini Batch API
      logger.info('Creating batch jobs for Gemini Batch API');
      const jobs = [];
      
      for (const garmentUrl of garmentUrls) {
        for (const poseUrl of poseUrls) {
          const job = {
            garment_url: garmentUrl,
            garment_category: selectedGarmentType,
            garment_presentation: 'flat_lay',
            model_image_url: poseUrl,
            output_quality: outputQuality
          };
          
          // Add background if available
          if (backgroundUrl) {
            job.background_url = backgroundUrl;
          }
          
          jobs.push(job);
        }
      }
      
      logger.info('Batch jobs created', { 
        jobCount: jobs.length,
        expectedGenerations: garmentUrls.length * poseUrls.length,
        endpoint: '/tryon/batch-process'
      });

      // Submit batch request
      logger.info('Submitting batch request to API');
      const batchResponse = await createBatchVirtualTryon({ jobs, headers });
      
      logger.info('Batch created successfully', { 
        batchId: batchResponse.batch_id,
        jobCount: jobs.length
      });
      
      // Navigate to batch status page with the batch ID
      const totalDuration = logger.endTimer('Batch Generation', batchStartTime);
      logger.info('Navigating to batch status page', { 
        batchId: batchResponse.batch_id,
        totalDuration: `${totalDuration}ms`
      });
      
      navigate('/batch-status', { 
        state: { 
          newBatch: true, 
          batchId: batchResponse.batch_id 
        } 
      });

    } catch (error) {
      logger.error('Batch generation failed', error, {
        garmentCount: garmentFiles.length,
        poseCount: selectedPoses.length,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status
      });
      
      let errorMsg = 'An unexpected error occurred during batch processing.';
      
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
      setErrorCode(error.response?.status);
    } finally {
      setLoading(false);
      setIsGenerating(false);
    }
  };

  const handleGenerate = async ()  => {
    const generationStartTime = logger.startTimer('Single Generation');
    logger.info('Starting generation', {
      mode: processingMode,
      hasModel: !!selectedModel,
      poseCount: selectedPoses.length,
      hasGarment: garmentFiles.length > 0,
      garmentType: selectedGarmentType
    });

    try {
      setLoading(true);
      setIsGenerating(true);
      setErrorMessage(null);
      setErrorCode(null);

      // Route to batch processing if in batch mode
      if (processingMode === 'batch') {
        logger.info('Routing to batch generation');
        await handleBatchGenerate();
        return;
      }

      // Single mode processing continues below
      logger.debug('Starting single mode processing');
      let personBase64 = null;
      let garmentBase64 = null;
      let backgroundBase64 = null;
      let posesBase64 = [];

      const fetchImageAsBase64 = async (imageUrl) => {
        try {
          logger.debug('Fetching image as base64', { imageUrl: imageUrl?.substring(0, 100) });
          const response = await fetch(imageUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
          }
          
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result.split(",")[1];
              logger.debug('Image converted to base64', { size: base64.length });
              resolve(base64);
            };
            reader.onerror = (error) => {
              logger.error('FileReader error', error);
              reject(error);
            };
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          logger.error('Error fetching image', error, { imageUrl: imageUrl?.substring(0, 100) });
          return null;
        }
      };

      if (selectedModel) {
        if (selectedModel.startsWith("data:image")) {
          personBase64 = selectedModel.split(",")[1];
        } else {
          personBase64 = await fetchImageAsBase64(selectedModel);
        }
      }

      if (selectedPoses.length > 0) {
        for (const pose of selectedPoses) {
          if (pose.startsWith("data:image")) {
            posesBase64.push(pose.split(",")[1]);
          } else {
            const base64 = await fetchImageAsBase64(pose);
            if (base64) {
              posesBase64.push(base64);
            }
          }
        }
      }

      if (garmentFiles && garmentFiles.length > 0) {
        logger.debug('Processing garment file');
        const garmentFile = garmentFiles[0]; // Get first garment in single mode
        if (garmentFile.startsWith("data:image")) {
          garmentBase64 = garmentFile.split(",")[1];
          logger.debug('Garment extracted from data URL', { size: garmentBase64.length });
        } else {
          garmentBase64 = await fetchImageAsBase64(garmentFile);
          logger.debug('Garment fetched from URL');
        }
      }

      if (selectedBackground) {
        if (selectedBackground.startsWith("data:image")) {
          backgroundBase64 = selectedBackground.split(",")[1];
        } else {
          backgroundBase64 = await fetchImageAsBase64(selectedBackground);
        }
      }

      if (!personBase64 || !garmentFiles || garmentFiles.length === 0 || !selectedGarmentType) {
        throw new Error(
          "Person image, garment image, and garment type are all required."
        );
      }

      const headers = {};
      if (currentUser) {
        headers["Authorization"] = `Bearer ${await currentUser.getIdToken()}`;
        headers["X-User-Id"] = currentUser.uid;
      }
      
      let response;
      let responseImages = [];
      let modelposes = [];
      if(posesBase64.length>0){
        modelposes = posesBase64;
      }else{
        modelposes = [personBase64];
      }

      if (aimodel === 1) {
        response = await postAiModel({
          garmentBase64,
          personBase64: modelposes,
          backgroundBase64,
          garmentCategory: selectedGarmentType,
          gender: modelTab === "male" ? "m" : "f",
          headers,
        });
      } else {
        try{

          // New implementation using /tryon/single endpoint
          logger.info('Starting virtual try-on with single endpoint', {
            hasGarment: !!garmentBase64,
            poseCount: modelposes.length,
            hasBackground: !!backgroundBase64,
            garmentType: selectedGarmentType
          });
          
          console.log('[Ai_model] Starting virtual try-on with new endpoint');
          console.log('[Ai_model] Input data:', {
            hasGarmentBase64: !!garmentBase64,
            garmentBase64Length: garmentBase64?.length,
            hasModelposes: modelposes.length > 0,
            modelposeCount: modelposes.length,
            hasBackgroundBase64: !!backgroundBase64,
            selectedGarmentType,
            hasHeaders: !!headers,
            headerKeys: Object.keys(headers),
          });

          // Convert base64 strings back to File objects for the new API
          const base64ToFile = (base64String, filename, mimeType = 'image/png') => {
            try {
              // Remove data URL prefix if present
              const base64Data = base64String.includes(',') 
                ? base64String.split(',')[1] 
                : base64String;
              
              const byteString = atob(base64Data);
              const ab = new ArrayBuffer(byteString.length);
              const ia = new Uint8Array(ab);
              for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
              }
              const blob = new Blob([ia], { type: mimeType });
              const file = new File([blob], filename, { type: mimeType });
              console.log(`[Ai_model] Created ${filename}:`, { 
                size: file.size, 
                type: file.type,
                originalLength: base64String.length,
                base64Length: base64Data.length 
              });
              return file;
            } catch (error) {
              console.error(`[Ai_model] Error creating file ${filename}:`, error);
              throw error;
            }
          };

          const garmentFile = base64ToFile(garmentBase64, 'garment.png', 'image/png');
          
          // Convert all model poses to files (API now accepts multiple model images)
          if (!modelposes || modelposes.length === 0) {
            throw new Error('At least one model pose is required');
          }
          
          const modelFiles = modelposes.map((pose, index) => 
            base64ToFile(pose, `model_${index}.png`, 'image/png')
          );
          
          const backgroundFile = backgroundBase64 ? base64ToFile(backgroundBase64, 'background.png', 'image/png') : null;

          console.log('[Ai_model] Prepared files:', {
            garmentFileSize: garmentFile?.size,
            modelFilesCount: modelFiles.length,
            modelFilesSizes: modelFiles.map(f => f.size),
            backgroundFileSize: backgroundFile?.size,
          });

          response = await singleVirtualTryon({
            garmentImage: garmentFile,
            garmentCategory: selectedGarmentType,
            garmentPresentation: 'flat_lay',
            modelImages: modelFiles,
            backgroundImage: backgroundFile,
            outputQuality: outputQuality,
            aspectRatio: aspectRatio,
            headers,
          });

          console.log('[Ai_model] Raw response received:', response);
          console.log('[Ai_model] Response analysis:', {
            hasResponse: !!response,
            responseType: typeof response,
            hasResults: !!(response && response.results),
            resultsCount: response?.results?.length || 0,
            requestId: response?.request_id,
            responseKeys: response ? Object.keys(response) : null,
            resultsStructure: response?.results?.[0] ? Object.keys(response.results[0]) : null,
          });

          // The new API returns: { request_id, output_quality, pose_preset?, results: [{ model_index, success, image?, image_url?, error? }], metadata }
          if (response && response.results && Array.isArray(response.results)) {
            // Extract images from successful results - prioritize image_url, fallback to base64
            responseImages = response.results
              .filter(result => result.success)
              .map(result => {
                // Prioritize image_url if available, otherwise use base64
                if (result.image_url) {
                  return result.image_url;
                } else if (result.image && result.image.data_base64) {
                  return result.image.data_base64;
                }
                return null;
              })
              .filter(img => img !== null); // Remove null entries
            
            console.log('[Ai_model] Successfully extracted images:', {
              totalResults: response.results.length,
              successfulResults: responseImages.length,
              failedResults: response.results.filter(r => !r.success).length,
              imageTypes: responseImages.map(img => img.startsWith('http') ? 'URL' : 'base64'),
              imageLengths: responseImages.map(img => img.length),
            });
            
            // Check if all results failed
            const failedResults = response.results.filter(r => !r.success);
            if (failedResults.length > 0) {
              console.warn('[Ai_model] Failed results:', failedResults.map(r => ({
                model_index: r.model_index,
                error: r.error,
              })));
              
              // If ALL results failed, throw a more user-friendly error
              if (failedResults.length === response.results.length && failedResults.length > 0) {
                // Extract the actual error message from the first failed result
                const firstError = failedResults[0].error;
                let userMessage = 'Image generation failed. ';
                
                // Parse common error patterns
                if (firstError && typeof firstError === 'string') {
                  if (firstError.includes('API key not valid') || firstError.includes('API_KEY_INVALID')) {
                    userMessage += 'Service configuration error. Please contact support.';
                  } else if (firstError.includes('quota') || firstError.includes('limit')) {
                    userMessage += 'Service quota exceeded. Please try again later.';
                  } else if (firstError.includes('timeout')) {
                    userMessage += 'Request timed out. Please try again.';
                  } else {
                    // Show a generic message but include the error for support
                    userMessage += 'Please try again or contact support if the issue persists.';
                  }
                }
                
                console.error('[Ai_model] All results failed:', userMessage);
                throw new Error(userMessage);
              }
            }
          } else {
            console.error('[Ai_model] Invalid response structure:', response);
          }

          /* Original implementation - preserved for restoration if needed
            response = await postSubmitVirtualTryon({
              garmentBase64,
              personBase64: modelposes,
              backgroundBase64,
              garmentCategory: selectedGarmentType,
              gender: modelTab === "male" ? "m" : "f",
              headers,
            });
           
            if(response.data && response.data.error_code === 0){
              let initialWait = 5000; // * modelposes.length;
              await asyncTimeout(initialWait);
              let jobIds = response.data.job_ids;
              let statusResponse = await checkVirtualTryonStatus(jobIds, headers);

              const completedResponses = statusResponse.data.jobs.filter(
                result => result.status === JOB_STATUS.COMPLETED
              );
              const uncompletedResponses = statusResponse.data.jobs.filter(
                result => result.status !== JOB_STATUS.COMPLETED
              );
              responseImages = completedResponses.flatMap(job => job.images || []);
              let statuses = uncompletedResponses.map(result => result.status);
              jobIds = uncompletedResponses.map(result => result.job_id);
              // Polling for status updates
              while (statuses.length > 0 && !statuses.includes(JOB_STATUS.COMPLETED) && !statuses.includes(JOB_STATUS.FAILED)) {
                await asyncTimeout(5000); // wait for 5 seconds before checking again
                statusResponse = await checkVirtualTryonStatus(jobIds, headers);
                const newCompletedResponses = statusResponse.data.jobs.filter(
                  result => result.status === JOB_STATUS.COMPLETED
                );
                responseImages = responseImages.concat(newCompletedResponses.flatMap(job => job.images || [])); 
                const newUncompletedResponses = statusResponse.data.jobs.filter(
                  result => result.status !== JOB_STATUS.COMPLETED
                );
                statuses = newUncompletedResponses.map(result => result.status);
                jobIds = newUncompletedResponses.map(result => result.job_id);
                console.log('Checking job statuses:', statuses);
              }

              response.data = { images: responseImages, status: JOB_STATUS.COMPLETED };  
          }else if(response.data && response.data.error_code === 2){
              // Specific handling for plan upgrade prompt
              setErrorMessage("You have exceeded your current plan's limits for AI Model usage. Please upgrade your plan.");
              setErrorCode(2);
          }         
          */
          }catch (error) {
            console.error('[Ai_model] Virtual try-on failed - Full error details:', {
              message: error.message,
              status: error.response?.status,
              statusText: error.response?.statusText,
              responseData: error.response?.data,
              stack: error.stack,
            });
            // Re-throw to be caught by outer catch
            throw error;
            // response = await postAiModel({
            //   garmentBase64,
            //   personBase64: modelposes,
            //   backgroundBase64,
            //   garmentCategory: selectedGarmentType,
            //   gender: modelTab === "male" ? "m" : "f",
            //   headers,
            // }); 
          }
      }
      
      // Handle response - adapted for new API structure
      console.log('[Ai_model] Validating response before navigation:', {
        hasResponse: !!response,
        responseImages: responseImages,
        responseImagesLength: responseImages?.length,
        responseStructure: response ? Object.keys(response) : null,
        firstImagePreview: responseImages?.[0]?.substring(0, 100), // First 100 chars of first image
        imageTypes: responseImages?.map(img => {
          if (typeof img === 'string') {
            if (img.startsWith('http')) return 'URL';
            if (img.startsWith('data:')) return 'data-url';
            return 'base64';
          }
          return typeof img;
        }),
      });

      if (
        response &&
        responseImages && responseImages.length > 0
      ) {
        const adaptedResponse = { images: responseImages };
        console.log('[Ai_model] Navigating with adapted response:', adaptedResponse);
        setApiResponse(adaptedResponse);
        navigate("/upload-processed-image", {
          state: {
            apiResponse: adaptedResponse,
            garmentType: selectedGarmentType,
          },
        });
      } else if (response && response.error_code === 2){
        // Specific handling for plan upgrade prompt
        setErrorMessage("You have exceeded your current plan's monthly limits for AI Model usage. Please upgrade your plan.");
      } else {
        // More detailed error message
        const errorDetails = {
          hasResponse: !!response,
          responseImagesCount: responseImages?.length || 0,
          responseKeys: response ? Object.keys(response) : [],
          response: response,
        };
        console.error("[Ai_model] Invalid response validation failed:", errorDetails);
        throw new Error(`Invalid response from API: ${JSON.stringify(errorDetails)}`);
      }
    } catch (error) {
      console.error("[Ai_model] Error processing images - Full details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        responseHeaders: error.response?.headers,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method,
        stack: error.stack,
      });
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const errorData = error.response.data;
        
        console.error("[Ai_model] Server error response:", {
          status,
          data: errorData,
          detail: errorData?.detail,
        });
        
        let errorMsg = `Server error (${status}): `;
        
        if (status === 413) {
          // Request Entity Too Large - nginx or server rejected the upload
          errorMsg = "Upload size limit exceeded. Please use smaller images (under 10MB each) or compress them before uploading.";
        } else if (status === 500) {
          errorMsg += "Internal server error. ";
          if (errorData?.detail) {
            errorMsg += Array.isArray(errorData.detail) 
              ? errorData.detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join(', ')
              : errorData.detail;
          } else if (errorData?.message) {
            errorMsg += errorData.message;
          } else {
            errorMsg += "Please check the console for details and try again.";
          }
        } else if (status === 422) {
          errorMsg += "Validation error. ";
          if (errorData?.detail) {
            errorMsg += Array.isArray(errorData.detail)
              ? errorData.detail.map(d => `${d.loc?.join('.')}: ${d.msg}`).join(', ')
              : errorData.detail;
          }
        } else {
          errorMsg += errorData?.message || errorData?.detail || "Unknown error";
        }
        
        setErrorMessage(errorMsg);
        setErrorCode(null);
      } else if (error.request) {
        setErrorMessage(
          "No response from server. Please check your internet connection and try again."
        );
        setErrorCode(null);
      } else {
        setErrorMessage(`An error occurred: ${error.message}. Please try again.`);
        setErrorCode(null);
      }
    } finally {
      setLoading(false);
      setIsGenerating(false);
    }

  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="max-w-5xl mx-auto">
            {/* Processing Mode Selector */}
            <div className="mb-8 bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Mode</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Single Mode */}
                <label className="flex-1 cursor-pointer">
                  <div className={`border-2 rounded-lg p-4 transition-all ${
                    processingMode === 'single' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="processingMode"
                        value="single"
                        checked={processingMode === 'single'}
                        onChange={(e) => {
                          setProcessingMode(e.target.value);
                          setGarmentFiles([]);
                          setSelectedGarmentType(null);
                          setErrorMessage(null);
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 mb-1">Single</div>
                        <div className="text-sm text-gray-600">
                          Process one garment at a time with instant results
                        </div>
                      </div>
                    </div>
                  </div>
                </label>

                {/* Batch Mode */}
                <label className={`flex-1 ${!hasBatchPermission ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <div className={`border-2 rounded-lg p-4 transition-all ${
                    processingMode === 'batch' 
                      ? 'border-blue-500 bg-blue-50' 
                      : !hasBatchPermission
                      ? 'border-gray-200 bg-gray-50 opacity-60'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="processingMode"
                        value="batch"
                        checked={processingMode === 'batch'}
                        onChange={(e) => {
                          if (hasBatchPermission) {
                            setProcessingMode(e.target.value);
                            setGarmentFiles([]);
                            setSelectedGarmentType(null);
                            setErrorMessage(null);
                          }
                        }}
                        disabled={!hasBatchPermission}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">Multiple (Batch)</span>
                          {!hasBatchPermission && (
                            <a
                              href="/pricing"
                              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>Upgrade</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          Process up to {MAX_GARMENTS} garments at once with async processing
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>

              {/* Batch Mode Advisory */}
              {processingMode === 'batch' && (
                <div className="mt-4 flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>Note:</strong> For optimal results, upload garments of the same type and for the same gender. 
                    Avoid mixing different garment categories (e.g., tops and skirts) in one batch.
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-start">
              <div className="md:col-span-3">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center mb-6">
                  1. Upload your garment photo{processingMode === 'batch' ? 's' : ''}
                </h3>
                <p className="text-sm text-gray-500 -mt-4 mb-4">
                  {processingMode === 'single' 
                    ? 'Select a clear front-facing image of your apparel'
                    : `Upload up to ${MAX_GARMENTS} garment images (same type and gender recommended)`
                  }
                </p>

                <div className="mb-8">
                  {processingMode === 'batch' && garmentFiles.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Selected Garments ({garmentFiles.length}/{MAX_GARMENTS})
                      </h4>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {garmentFiles.map((garment, index) => (
                          <GarmentCard
                            key={index}
                            image={garment}
                            index={index}
                            onRemove={() => removeGarment(index)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {(processingMode === 'single' && garmentFiles.length > 0) ? (
                    <ImageDropPreview file={garmentFiles[0]} onDrop={onGarmentDrop} />
                  ) : (processingMode === 'batch' && garmentFiles.length >= MAX_GARMENTS) ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                      <p className="text-gray-600">Maximum {MAX_GARMENTS} garments reached</p>
                      <p className="text-sm text-gray-500 mt-1">Remove a garment to add more</p>
                    </div>
                  ) : (
                    <DropzoneArea
                      onDrop={onGarmentDrop}
                      acceptedFiles={garmentFiles}
                      multiple={processingMode === 'batch'}
                      uploadText={
                        processingMode === 'single'
                          ? "Drag and drop a photo file here to upload"
                          : `Drag and drop up to ${MAX_GARMENTS - garmentFiles.length} photos here (${garmentFiles.length}/${MAX_GARMENTS})`
                      }
                    />
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                {/* Align this title with the upload title visually */}
                <h3 className="text-xl font-semibold text-gray-900 flex items-center mb-6">                  
                  2. Select Garment Type
                </h3>
                <p className="text-sm text-gray-500 -mt-4 mb-4">Help us accurately place your garment on the model</p>

                <div className="grid grid-cols-2 gap-4">
                  {garmentTypes.map((type) => {
                    const isSelected = selectedGarmentType === type.id;
                    return (
                      <div
                        key={type.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={isSelected}
                        onClick={() => setSelectedGarmentType(type.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedGarmentType(type.id);
                          }
                        }}
                        className={`cursor-pointer select-none flex flex-col items-center justify-center rounded-lg p-4 border transition ${
                          isSelected
                            ? "bg-blue-50 border-blue-500 shadow-md"
                            : "bg-white border-gray-200 hover:shadow-sm"
                        }`}
                      >
                        <div className="text-3xl mb-3">{type.icon}</div>
                        <div className="text-sm font-medium text-gray-700">
                          {type.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl text-gray-900 flex items-center">
                <span>
                  <img
                    src={modelIcon}
                    alt="ai model icon"
                    className="mr-2 w-9 h-9"
                  />
                </span>
                Select Model
              </h3>
              {/*<button className="text-[#364152] rounded-[8px] px-[16px] py-[8px] hover:text-gray-700 shadow-xs border-[1px] border-[#CDD5DF] bg-[#FFFFFF] flex items-center">
                <Upload className="w-4 h-4 mr-1" />
                Or upload from Gallery
              </button>*/}
            </div>

            <p className="text-gray-600 mb-6">Select a model and at least one pose to proceed (you can select multiple poses).</p>

            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => setModelTab("female")}
                className={`px-4 py-2 font-medium ${
                  modelTab === "female"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "cursor-pointer text-gray-500"
                }`}
              >
                Female
              </button>
              <button
                onClick={() => setModelTab("male")}
                className={`px-4 py-2 font-medium ${
                  modelTab === "male"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "cursor-pointer text-gray-500"
                }`}
              >
                Male
              </button>
              <button
                onClick={() => setModelTab("poses")}
                disabled={!selectedModel}
                className={`px-4 py-2 font-medium ${
                  modelTab === "poses"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "cursor-pointer text-gray-500"
                } disabled:text-gray-300 disabled:cursor-not-allowed`}
              >
                Poses
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {modelTab === "male" &&
                  maleModels.map((model, index) => (
                    <ModelCard
                      key={index}
                      image={model.image}
                      isSelected={selectedModel === model.image}
                      onClick={() => {
                        setSelectedModel(model.image);
                        setSelectedModelId(model.id);
                        setSelectedPoses([]); // Clear poses when selecting a different model
                        setModelTab('poses'); // Auto-navigate to poses tab
                      }}
                    />
                  ))}
                {modelTab === "female" &&
                  femaleModels.map((model, index) => (
                    <ModelCard
                      key={index}
                      image={model.image}
                      isSelected={selectedModel === model.image}
                      onClick={() => {
                        setSelectedModel(model.image);
                        setSelectedModelId(model.id);
                        setSelectedPoses([]); // Clear poses when selecting a different model
                        setModelTab('poses'); // Auto-navigate to poses tab
                      }}
                    />
                  ))}
                {modelTab === "poses" &&
                  posesImages
                    .filter(pose => selectedModelId && pose.id.startsWith(selectedModelId))
                    .map((pose, index) => (
                      <ModelCard
                        key={index}
                        image={pose.image}
                        isSelected={selectedPoses.includes(pose.image)}
                        onClick={() => handlePoseSelection(pose.image)}
                      />
                    ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="max-w-5xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center mb-6">
              <span className="bg-blue-100 text-blue-600 p-2 rounded-lg mr-3">
                🖼️
              </span>
              Pick a background
            </h3>

            {/* { <div className="mb-8">
              <DropzoneArea
                onDrop={onBackgroundDrop}
                acceptedFiles={backgroundFiles}
                uploadText="Drag and drop a photo file here to upload"
              />
            </div> } */}

            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900">
                Select Background (Optional):
              </h4>
             {/* <button className="text-gray-500 hover:text-gray-700 flex items-center">
                <Upload className="w-4 h-4 mr-1" />
                Or upload from Gallery
              </button> */}
            </div>

            <div className="max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {backgroundImages.map((bg, index) => (
                  <ModelCard
                    key={index}
                    image={bg}
                    isSelected={selectedBackground === bg}
                    onClick={() => setSelectedBackground(bg)}
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="max-w-2xl mx-auto text-center">
            {!generatedImages ? (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  {processingMode === 'batch' ? 'Review & Submit Batch' : 'Generate Your Look'}
                </h3>
                <p className="text-gray-600 mb-8">
                  {processingMode === 'batch' ? (
                    <>
                      You're about to create a batch job with:<br />
                      <strong>{garmentFiles.length} garments × {selectedPoses.length || 1} pose{(selectedPoses.length || 1) > 1 ? 's' : ''} = {getTotalGenerations()} total generations</strong><br /><br />
                      Your batch will be processed asynchronously. You can track progress on the Batch Status page.
                    </>
                  ) : (
                    <>
                      Click the button below to generate your AI model image<br />
                      Please be patient as we generate your image <br /><br />
                      Note: The time it takes depends on the number of poses you selected.
                    </>
                  )}
                </p>

                {/* Batch Usage Warning */}
                {processingMode === 'batch' && !hasEnoughUsage() && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-800 text-left">
                        <strong>Insufficient Usage:</strong> You need {getTotalGenerations()} generations but only have {getAvailableGenerations()} available. 
                        Please upgrade your plan or reduce the number of garments/poses.
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Settings Grid - Mobile Responsive */}
                <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto">
                  {/* Output Quality Dropdown */}
                  <div className="text-left">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Output Quality
                    </label>
                    <select
                      value={outputQuality}
                      onChange={(e) => {
                        const selectedQuality = e.target.value;
                        if (qualityValue[selectedQuality] > qualityValue[maxQuality]) {
                          setQualityUpgradeMessage(
                            `${selectedQuality} output is not available in your subscription. Please upgrade to access ${selectedQuality} quality.`
                          );
                          // Keep the current selection at max quality
                          setOutputQuality(maxQuality);
                        } else {
                          setQualityUpgradeMessage('');
                          setOutputQuality(selectedQuality);
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 cursor-pointer"
                    >
                      <option value="1K">1K (1024×1024)</option>
                      <option value="2K" disabled={qualityValue['2K'] > qualityValue[maxQuality]}>
                        2K (2048×2048){qualityValue['2K'] > qualityValue[maxQuality] ? ' - Upgrade Required' : ''}
                      </option>
                      <option value="4K" disabled={qualityValue['4K'] > qualityValue[maxQuality]}>
                        4K (4096×4096){qualityValue['4K'] > qualityValue[maxQuality] ? ' - Upgrade Required' : ''}
                      </option>
                    </select>
                    {qualityUpgradeMessage && (
                      <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          {qualityUpgradeMessage}
                        </p>
                        <a
                          href="/pricing"
                          className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 inline-block"
                        >
                          View Pricing Plans
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Aspect Ratio Dropdown */}
                  <div className="text-left">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aspect Ratio
                    </label>
                    <select
                      value={aspectRatio}
                      onChange={(e) => setAspectRatio(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 cursor-pointer"
                    >
                      <option value="1:1">1:1 (Square)</option>
                      <option value="3:4">3:4 (Portrait)</option>
                      <option value="4:3">4:3 (Landscape)</option>
                      <option value="16:9">16:9 (Widescreen)</option>
                    </select>
                  </div>
                </div>
                
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  aria-busy={loading}
                  className={`bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                    loading ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700 cursor-pointer"
                  }`}
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                      {processingMode === 'batch' ? 'Submitting Batch...' : 'Generating...'}
                    </>
                  ) : (
                    processingMode === 'batch' ? 'Submit Batch' : 'Generate Look'
                  )}
                </button>
              </div>
            ) : (
              <div>
                <div className="bg-white rounded-lg p-4 shadow-lg mb-6">
                  <img
                    src={generatedImages}
                    alt="Generated Look"
                    className="w-full max-w-md mx-auto rounded-lg"
                  />
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600 mb-6">
                  <span>Monthly Limit: 1/5</span>
                  <button className="cursor-pointer flex items-center text-gray-500 hover:text-gray-700">
                    <Upload className="w-4 h-4 mr-1" />
                    Save to Gallery
                  </button>
                </div>
                <button
                  onClick={() => setGeneratedImages(null)}
                  className="cursor-pointer bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Generate Again
                </button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex-1 h-full overflow-auto relative z-10 bg-gradient-to-b from-slate-50 to-white">
      <Header title={"AI Model"} /> 

      <section className="py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="mb-5">
            <h2 className="inline-block  text-[24px] rounded-full text-xs text-[#121212]">
              Fit your garments on an AI model
            </h2>
            <p className="text-[#697586] text-[16px]">
              Create professionally looking images of your garments on Virtual
              AI models of different race, color and ethnicity, without a
              photoshoot.
            </p>
          </div>

          <div className="bg-[#FFFFFF] border-[1px] border-[#EEF2F6] rounded-[16px] p-6">
            <StepIndicator currentStep={currentStep} steps={steps} />

            <div className="bg-white rounded-[16px] shadow-sm border-[1px] border-[#EEF2F6] p-6 mb-8">
              {renderStepContent()}
            </div>

            <div className="flex justify-between mb-8 md:mb-0">
              {currentStep > 0 ? (
                <button
                  onClick={handleBack}
                  disabled={isGenerating}
                  className={`flex items-center px-6 py-2 rounded-lg font-medium transition-colors ${
                    isGenerating
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100 cursor-pointer"
                  }`}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {currentStep > 0 ? `Previous: ${steps[currentStep - 1]}` : 'Back'}
                </button>
              ) : (
                <div />
              )}

              {currentStep < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={!canProceedToNext()}
                  className={`px-8 py-2 rounded-lg font-medium transition-colors ${
                    canProceedToNext()
                      ? "bg-[#4F39F6] text-white hover:bg-violet-800 cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {currentStep === 0 ? (
                    <span className="flex items-center">
                      <span className="mr-2">Next: Select Model</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  ) : currentStep === 1 ? (
                    <span className="flex items-center">
                      <span className="mr-2">Next: Pick a background</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  ) : currentStep === 2 ? (
                    <span className="flex items-center">
                      <span className="mr-2">Next: Finish</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </span>
                  ) : (
                    "Continue"
                  )}
                </button>
              ) : (
                currentStep === steps.length - 1 &&
                generatedImages && (
                  <button
                    onClick={handleGenerate}
                    className="bg-[#4F39F6] text-white px-8 py-2 rounded-lg font-medium hover:bg-violet-800 transition-colors flex items-center"
                  >
                    <span className="cursor-pointer mr-2">✨</span>
                    Generate Your Look
                  </button>
                )
              )}
            </div>
            {errorMessage && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{errorMessage}</p>
                {errorCode === 2 && (
                  <a
                    href="/billing"
                    className="text-red-700 underline hover:text-red-800"
                  >
                    Upgrade your plan here
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ai_model;
